/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-shadow */

import { clone, extend } from "lodash";
import React, {
	createContext,
	ReactElement,
	useCallback,
	useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { Select, react } from "uikit";

import Option from "./components/Option";
import Selected from "./components/Selected";

interface OptionType<T> {
	key: Select.Value;
	label: string;
	value: T;
}

const capitalizeFirstLetter = (value: string) => {
	if (!value) return value;
	return value.charAt(0).toUpperCase() + value.slice(1);
};

const Context = createContext<LocalMultiSelect.Context.Value | null>(null);

const LocalMultiSelect = extend(
	react.inputify(
		// eslint-disable-next-line prettier/prettier
		<OptionValue extends Select.Value>({
			value,

			all = false,
			allLabel,
			options,
			placeholder,

			onChange,

			onSelect,
			onUnselect,

			...props
		}: LocalMultiSelect.InternalProps<OptionValue>) => {
			const { t } = useTranslation();

			const localizedAllLabel = useMemo(
				() =>
					capitalizeFirstLetter(
						allLabel ?? t("filter.options.all") ?? "all",
					),
				[allLabel, t],
			);

			const areAllOptionsSelected = useMemo(
				() =>
					options?.every((option) => value?.includes(option.key)) ??
					false,
				[options, value],
			);

			const processedOptions = useMemo<
				OptionType<OptionValue | "all">[] | undefined
			>(
				() =>
					all
						? [
								{
									key: "all",
									label: localizedAllLabel,
									value: "all",
								},
								...(options ?? []),
						  ]
						: options,
				[all, localizedAllLabel, options],
			);

			const selectOnSelect = useCallback(
				(option: OptionType<OptionValue | "all">) => {
					if (option.value === "all") {
						if (areAllOptionsSelected) onChange([]);
						else
							onChange(
								options?.map(
									(sourceOption) => sourceOption.key,
								) ?? [],
							);

						return;
					}

					const newValue = clone(value) ?? [];

					if (newValue?.includes(option.key)) {
						onUnselect?.(option as OptionType<OptionValue>);

						newValue.splice(newValue.indexOf(option.key), 1);
					} else {
						onSelect?.(option as OptionType<OptionValue>);

						newValue.push(option.key);
					}

					if (options)
						newValue.sort(
							(selected1, selected2) =>
								options.findIndex(
									(sourceOption) =>
										sourceOption.key === selected1,
								) -
								options.findIndex(
									(sourceOption) =>
										sourceOption.key === selected2,
								),
						);

					onChange(newValue);
				},
				[
					value,
					areAllOptionsSelected,
					options,
					onChange,
					onSelect,
					onUnselect,
				],
			);

			return (
				<Context.Provider
					value={{
						value,
						all,
						allLabel: localizedAllLabel,
						areAllOptionsSelected,
						placeholder,
						options,
						onClick: selectOnSelect,
					}}
				>
					<Select
						{...props}
						options={processedOptions}
						closeOnSelect={false}
						Selected={Selected}
						Option={Option}
						onSelect={(option) =>
							selectOnSelect(
								option as OptionType<OptionValue | "all">,
							)
						}
					/>
				</Context.Provider>
			);
		},
	) as <OptionValue>(
		props: LocalMultiSelect.Props<OptionValue>,
	) => ReactElement | null,
	{
		Context,
	},
);

declare namespace LocalMultiSelect {
	type Value = Select.Value[];

	interface PropsBase<OptionValue>
		extends Omit<Select.PropsBase<OptionValue>, "Option" | "Selected"> {
		all?: boolean;
		allLabel?: string;

		onSelect?: (option: OptionType<OptionValue>) => void;
		onUnselect?: (option: OptionType<OptionValue>) => void;
	}

	type InternalProps<OptionValue> = PropsBase<OptionValue> &
		react.InputifyComponentProps<Value>;

	type Props<OptionValue> = PropsBase<OptionValue> &
		react.InputifiedComponentProps<Value>;
}

declare namespace LocalMultiSelect.Context {
	interface Value {
		value?: LocalMultiSelect.Value;

		all: boolean;
		allLabel?: string;
		areAllOptionsSelected: boolean;
		placeholder?: string;
		options: LocalMultiSelect.Props<unknown>["options"];
		disabled?: boolean;

		onClick: (option: OptionType<any>) => void;
	}
}

export default LocalMultiSelect;
